import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, ApiError } from "@/api.js";
import { Button } from "@/components/ui/button";
import {
  clearSession,
  getImpersonation,
  IMPERSONATION_EVENT,
  setImpersonation,
  type ImpersonationState,
} from "@/auth.js";

// Sticky red banner rendered at the top of every AppLayout page when the
// caller is currently impersonating a tenant. Re-renders on storage events
// so opening a second tab with the original account cleans up cleanly.

// Server-emitted timestamps are either SQLite "YYYY-MM-DD HH:MM:SS" (no
// timezone) or client-set ISO strings with "Z". Normalise both to a
// valid parseable date before formatting.
function formatStartedAt(raw: string): string {
  const iso = raw.includes("T")
    ? raw.endsWith("Z") || /[+-]\d\d:?\d\d$/.test(raw)
      ? raw
      : raw + "Z"
    : raw.replace(" ", "T") + "Z";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

export function ImpersonationBanner() {
  const [state, setState] = useState<ImpersonationState | null>(() =>
    getImpersonation(),
  );
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    function onChanged() {
      setState(getImpersonation());
    }
    // `storage` fires cross-tab; IMPERSONATION_EVENT fires same-tab.
    // Both matter: a second tab ending impersonation should clean the
    // first tab, and the /auth/me hydration that runs in UserMenu on
    // this tab must surface into the banner without a manual reload.
    window.addEventListener("storage", onChanged);
    window.addEventListener(IMPERSONATION_EVENT, onChanged);
    return () => {
      window.removeEventListener("storage", onChanged);
      window.removeEventListener(IMPERSONATION_EVENT, onChanged);
    };
  }, []);

  if (!state) return null;

  async function endImpersonation() {
    setBusy(true);
    try {
      await api.endImpersonation();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "failed to end cleanly");
    } finally {
      // Either way, wipe the local session — the caller must sign back in
      // as themselves. This is the safe default: it avoids any chance the
      // tokens linger.
      clearSession();
      setImpersonation(null);
      nav("/login", { replace: true });
      setBusy(false);
    }
  }

  const readOnly = state.view_as_readonly !== false;
  return (
    <div
      role="alert"
      className="flex items-center justify-between gap-3 bg-destructive px-4 py-2 text-sm text-destructive-foreground"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
        <span className="font-semibold">Impersonating</span>
        <span>
          Viewing <code className="font-mono">{state.target_slug}</code>{" "}
          as <code className="font-mono">{state.view_as_role ?? "tenant_admin"}</code>{" "}
          {readOnly ? (
            <span className="rounded bg-destructive-foreground/20 px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
              read-only
            </span>
          ) : (
            <span className="rounded bg-destructive-foreground/30 px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
              write mode
            </span>
          )}
          . All actions audited under your identity.
        </span>
        {state.reason_category && (
          <span className="text-xs opacity-90">
            · category: <em>{state.reason_category}</em>
          </span>
        )}
        {state.reason && (
          <span className="text-xs opacity-90">
            · reason: <em>{state.reason}</em>
          </span>
        )}
        {state.started_at && (
          <span className="text-xs opacity-80">
            · started {formatStartedAt(state.started_at)}
          </span>
        )}
        {state.expires_at && (
          <span className="text-xs opacity-80">
            · expires {formatStartedAt(state.expires_at)}
          </span>
        )}
      </div>
      <Button
        size="sm"
        variant="outline"
        className="border-destructive-foreground/40 bg-transparent text-destructive-foreground hover:bg-destructive-foreground/10"
        onClick={endImpersonation}
        disabled={busy}
      >
        {busy && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
        Exit impersonation
      </Button>
    </div>
  );
}
